<template>
  <v-card
    ref="viewContainer"
    elevation="6"
    :class="[
      'view-Container d-flex flex-column',
      {
        closed: minimized,
      },
    ]"
    :style="viewStyle"
  >
    <v-toolbar ref="viewToolbar" class="text-font" color="toolbar" :height="toolbarHeight">
      <v-btn size="small" icon="mdi-unfold-more-horizontal" :color="minimized ? 'font' : 'primary'" @click="minMax()">
      </v-btn>
      <slot :min-max="minMax" :minimized="minimized" name="toolbarContent"></slot>
    </v-toolbar>
    <slot :key="propertiesKey" :show="!minimized" name="content"></slot>
  </v-card>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import DragHandler from "../Editor/services/drag-handler";
import { ContainerAlwaysVisibleResizeHandler } from "../Editor/services/resize-handler";
import { LastMaxSize, toolbarHeight, CardPosition, foldDirection } from "../Editor/utils/resizeCard";
import { useDisplay } from "vuetify";
import RappidContext from "../Editor/rappid/rappid-context";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const baseHeight = "260px";
const baseWidth = "250px";

const compProps = withDefaults(
  defineProps<{
    cardType: "top" | "bottom";
    xInitPos: "10px" | "270px" | "530px" | "740px";
    maximizedInitWidth?: string;
    initAlwaysMinimized?: boolean;
    forceMaximize?: boolean;
    maximized: boolean;
  }>(),
  {
    maximizedInitWidth: "min-content",
    initAlwaysMinimized: false,
  }
);

const emit = defineEmits(["update:forceMaximize", "update:maximized"]);

watch(
  () => compProps.forceMaximize,
  (newVal) => {
    if (newVal) {
      maximize();
      emit("update:forceMaximize", false);
    }
  }
);

const viewContainer = ref(null);

const viewToolbar = ref(null);

const maxSize = reactive<LastMaxSize>({
  width: compProps.maximizedInitWidth,
  height: baseHeight,
});

const lastMaxSize = reactive<LastMaxSize>({
  width: compProps.maximizedInitWidth,
  height: baseHeight,
});

const position = reactive<CardPosition>({
  xLeft: compProps.xInitPos,
  yTop: "unset",
  yBottom: compProps.cardType === "bottom" ? "20px" : "unset",
});

const { height, lgAndDown, mdAndDown } = useDisplay();

const minimized = computed({
  get() {
    return !compProps.maximized;
  },
  set(value: boolean) {
    emit("update:maximized", !value);
  },
});

const viewStyle = computed<string>(() => {
  if (minimized.value) {
    return `
      height: ${toolbarHeight};
      width: ${baseWidth};
      resize: none;
      left: ${position.xLeft};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  } else {
    return `
      height: ${maxSize.height};
      width: ${maxSize.width};
      resize: both;
      left: ${position.xLeft};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  }
});

const resizeHandler = ref<ContainerAlwaysVisibleResizeHandler>(new ContainerAlwaysVisibleResizeHandler(maxSize));

const dragContent = ref<HTMLElement>();

const propertiesKey = ref<number>(1);

onMounted(() => {
  const dragTarget = viewToolbar.value.$el as HTMLElement;
  dragContent.value = viewContainer.value.$el as HTMLElement;
  new DragHandler(dragTarget, dragContent.value, position).enableDragging();

  resizeHandler.value.observeContainer(dragContent.value);
  if (compProps.initAlwaysMinimized) {
    minimized.value = true;
  } else {
    minimized.value = lgAndDown.value;
  }

  rappidContext.commandManager.on("stack:undo", rerenderContent);
  rappidContext.commandManager.on("stack:redo", rerenderContent);
});

function rerenderContent() {
  propertiesKey.value++;
}

watch(mdAndDown, (newVal: boolean) => {
  if (newVal) {
    maxSize.width = compProps.maximizedInitWidth;
    maxSize.height = baseHeight;
    if (!minimized.value) {
      minimize();
    }
  }
});

onBeforeUnmount(() => {
  resizeHandler.value.disconnect();

  rappidContext.commandManager.off("stack:undo", rerenderContent);
  rappidContext.commandManager.off("stack:redo", rerenderContent);
});

function minMax() {
  minimized.value ? maximize() : minimize();
}

function minimize() {
  lastMaxSize.width = maxSize.width;
  lastMaxSize.height = maxSize.height;

  if (foldDirection(dragContent, "min", height.value) === "upwards") {
    position.yTop = dragContent.value.getBoundingClientRect().top + "px";
  } else {
    position.yTop = dragContent.value.getBoundingClientRect().bottom - parseInt(toolbarHeight) + "px";
  }

  minimized.value = true;
}

function maximize() {
  maxSize.width = lastMaxSize.width;
  maxSize.height = lastMaxSize.height;
  if (foldDirection(dragContent, "max", height.value) === "upwards") {
    position.yTop =
      dragContent.value.getBoundingClientRect().top + parseInt(toolbarHeight) - parseInt(maxSize.height) + "px";
  } else {
    position.yTop = dragContent.value.getBoundingClientRect().top + "px";
  }

  minimized.value = false;
}
</script>

<style scoped>
.view-Container {
  position: absolute;
  min-height: 150px;
  min-width: v-bind(baseWidth);
  max-height: 500px;
  max-width: 500px;
  z-index: 2;
}

.view-Container.closed {
  min-height: v-bind(toolbarHeight);
}

/* TODO check media queries and compare as well with other views (statechartdefinitions, problemsview, chatcontainer) */

/* Media queries */
/* @media (max-width: 599.98px) {
  .view-Container {
    top: unset;
    left: unset;
    right: 30px;
    bottom: 0;
  }
}
@media (max-height: 564.98px) {
  .view-Container {
    height: v-bind(baseHeight);
  }
  .view-Container {
    top: unset;
    left: unset;
    right: 30px;
    bottom: 0;
  }
} */
</style>
